import uniq from 'lodash/uniq';

import type { OpportunityRelatedRecords } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { SALESFORCE_FIELD_MAX_LENGTH, previewName } from '../constants';
import type { BlackoutDraft } from '../tabs/blackouts/types';
import { egsRoomName, egsRooms, getEgsRecordName, knownEgsRooms } from '../tabs/egs/egsModel';
import type { EgsDraftPayload } from '../tabs/egs/types';
import { getAvailableNights, getPackageRooms, packageMatchesApplyTo } from '../tabs/inclusions/inclusionsModel';
import type { InclusionDraftPayload } from '../tabs/inclusions/types';
import type { OverrideSet } from '../tabs/overrides/types';
import { PACKAGE_CATEGORIES } from '../tabs/packageOptions/packageCategories';
import { deriveExtraNightSell, isFallbackAssetId } from '../tabs/packageOptions/packageOptionsModel';
import type { CreatedAsset, OptionState } from '../tabs/packageOptions/types';
import type { PropertySetupValues } from '../tabs/propertySetup/types';
import type { SurchargePeriod, Weekday } from '../tabs/surcharges/types';
import type { PricingOptionIndex } from '../types';

type FieldValue = string | number | boolean | null;
type Fields = Record<string, FieldValue>;

// `ref` must stay the same across submissions: svc-offer patches the record it maps to.
export interface SalesforceSubmission {
  opportunity: Fields;
  room_assets: Array<{ ref: string; fields: Fields }>;
  inclusion_assets: Array<{ ref: string; fields: Fields }>;
  blackouts: Array<{ ref: string; fields: Fields }>;
  extra_guest_surcharges: Array<{ ref: string; fields: Fields }>;
  override_tiers: Array<{ ref: string; fields: Fields }>;
  deal_offers: Array<{ ref: string; room_asset: string | null; fields: Fields }>;
  surcharge_periods: Array<{ ref: string; room_asset: string | null; fields: Fields }>;
  // svc-offer keys these by `${deal_offer}::${ref}`; don't prefix the deal option here.
  inclusions: Array<{ ref: string; deal_offer: string; asset: string; fields: Fields }>;
  bundled_experiences: Array<{ ref: string; deal_offer: string; fields: Fields }>;
}

export interface SalesforceSubmissionInput {
  preferredOption: PricingOptionIndex;
  propertySetup: PropertySetupValues | null;
  optionState: OptionState;
  roomAssets: Array<CreatedAsset>;
  inclusionDrafts: Array<InclusionDraftPayload>;
  inclusionAssets: Array<CreatedAsset>;
  blackouts: Array<Pick<BlackoutDraft, 'id' | 'name' | 'startDate' | 'endDate'>>;
  surchargePeriods: ReadonlyArray<SurchargePeriod>;
  egs: Array<EgsDraftPayload>;
  opportunity: OpportunityRelatedRecords | undefined;
  previousOpportunity?: Fields;
  overrides: OverrideSet | undefined;
  forecast: { roomNights: number | null; ttvAud: number | null; grossMarginAud: number | null } | null;
}

const WEEKDAY_NAMES: Record<Weekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

const COMMISSION_BASIS_LABELS: Record<OverrideSet['basis'], string> = {
  vendor_revenue: 'Vendor Revenue',
  room_nights: 'Room Nights',
};

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const numberOrNull = (value: string | undefined) => {
  const parsed = Number(value);
  return value?.trim() && Number.isFinite(parsed) ? parsed : null;
};

// Blanks are omitted so a submission never clears Salesforce values it didn't set.
const withoutBlanks = (fields: Record<string, FieldValue | undefined>): Fields =>
  Object.fromEntries(
    Object.entries(fields).filter((entry): entry is [string, FieldValue] => entry[1] != null && entry[1] !== ''),
  );

// The room name is truncated first so nights, occupancy and suffix still tell deal options apart.
export function dealOptionName(nights: number, roomName: string, occupancy: string, suffix?: string) {
  const maxLength = SALESFORCE_FIELD_MAX_LENGTH.dealOptionName;
  const prefix = `${nights}N `;
  const rest = [` ${occupancy}`, suffix?.trim()].filter(Boolean).join(' - ');
  const name = `${prefix}${roomName}${rest}`;
  if (name.length <= maxLength) {
    return name;
  }
  const roomBudget = maxLength - prefix.length - rest.length - 1;
  return roomBudget > 0 ? `${prefix}${roomName.slice(0, roomBudget)}…${rest}` : `${name.slice(0, maxLength - 1)}…`;
}

type RecordKind = Exclude<keyof SalesforceSubmission, 'opportunity'>;

type SubmittedRecord = {
  ref: string;
  fields: Fields;
  room_asset?: string | null;
  deal_offer?: string;
  asset?: string;
};

const NAME_LIMITS: Partial<Record<RecordKind, { label: string; maxLength: number }>> = {
  room_assets: { label: 'Room asset', maxLength: SALESFORCE_FIELD_MAX_LENGTH.assetName },
  inclusion_assets: { label: 'Inclusion asset', maxLength: SALESFORCE_FIELD_MAX_LENGTH.assetName },
  deal_offers: { label: 'Deal option', maxLength: SALESFORCE_FIELD_MAX_LENGTH.dealOptionName },
  inclusions: { label: 'Inclusion', maxLength: SALESFORCE_FIELD_MAX_LENGTH.inclusionName },
  blackouts: { label: 'Blackout', maxLength: SALESFORCE_FIELD_MAX_LENGTH.blackoutName },
  surcharge_periods: { label: 'Surcharge period', maxLength: SALESFORCE_FIELD_MAX_LENGTH.surchargeName },
  override_tiers: { label: 'Override tier', maxLength: SALESFORCE_FIELD_MAX_LENGTH.overrideName },
};

// svc-offer never deletes assets.
const DELETABLE_KINDS: Array<RecordKind> = [
  'deal_offers',
  'inclusions',
  'bundled_experiences',
  'blackouts',
  'surcharge_periods',
  'override_tiers',
];

const recordName = (record: SubmittedRecord) => String(record.fields.name ?? '');

// Must match svc-offer's record keys.
const submittedKey = (record: SubmittedRecord) =>
  record.deal_offer ? `${record.deal_offer}::${record.ref}` : record.ref;

interface SubmissionWithoutLongNames {
  submission: SalesforceSubmission;
  excludedReasons: Array<string>;
  excludedFromLastSubmission: Array<string>;
}

export function excludeRecordsWithLongNames(
  submission: SalesforceSubmission,
  lastSubmission?: SalesforceSubmission,
): SubmissionWithoutLongNames {
  const recordsOf = (from: SalesforceSubmission | undefined, kind: RecordKind) =>
    (from?.[kind] ?? []) as Array<SubmittedRecord>;
  const isTooLong = (kind: RecordKind, record: SubmittedRecord) =>
    !!NAME_LIMITS[kind] && recordName(record).length > NAME_LIMITS[kind].maxLength;
  const refsWithLongNames = (kind: RecordKind) =>
    new Set(
      recordsOf(submission, kind)
        .filter((record) => isTooLong(kind, record))
        .map(({ ref }) => ref),
    );

  const excludedRoomAssets = refsWithLongNames('room_assets');
  const excludedInclusionAssets = refsWithLongNames('inclusion_assets');
  const excludedDealOffers = new Set(
    submission.deal_offers
      .filter((record) => isTooLong('deal_offers', record) || excludedRoomAssets.has(record.room_asset))
      .map(({ ref }) => ref),
  );

  const dependentReason = (kind: RecordKind, record: SubmittedRecord): string | null => {
    const name = `"${previewName(recordName(record))}"`;
    if ((kind === 'deal_offers' || kind === 'surcharge_periods') && excludedRoomAssets.has(record.room_asset)) {
      return `${NAME_LIMITS[kind].label} ${name}: its room asset's name is too long.`;
    }
    if ((kind === 'inclusions' || kind === 'bundled_experiences') && excludedDealOffers.has(record.deal_offer)) {
      return `${kind === 'inclusions' ? 'Inclusion' : 'Bundled experience'} ${name}: its deal option is left out.`;
    }
    if (kind === 'inclusions' && excludedInclusionAssets.has(record.asset)) {
      return `Inclusion ${name}: its inclusion asset's name is too long.`;
    }
    return null;
  };
  const exclusionReason = (kind: RecordKind, record: SubmittedRecord): string | null => {
    if (!isTooLong(kind, record)) {
      return dependentReason(kind, record);
    }
    const { label, maxLength } = NAME_LIMITS[kind];
    return `${label} "${previewName(recordName(record))}" is ${recordName(record).length} characters; Salesforce allows ${maxLength}.`;
  };

  const reasonByKey = new Map<string, string>();
  const kept = { ...submission };
  (Object.keys(submission).filter((kind) => kind !== 'opportunity') as Array<RecordKind>).forEach((kind) => {
    (kept as Record<RecordKind, Array<SubmittedRecord>>)[kind] = recordsOf(submission, kind).filter((record) => {
      const reason = exclusionReason(kind, record);
      if (reason) {
        reasonByKey.set(`${kind}|${submittedKey(record)}`, reason);
      }
      return !reason;
    });
  });
  const usedRoomAssets = new Set([...kept.deal_offers, ...kept.surcharge_periods].map(({ room_asset }) => room_asset));
  const usedInclusionAssets = new Set(kept.inclusions.map(({ asset }) => asset));
  kept.room_assets = kept.room_assets.filter(({ ref }) => usedRoomAssets.has(ref));
  kept.inclusion_assets = kept.inclusion_assets.filter(({ ref }) => usedInclusionAssets.has(ref));

  const excludedFromLastSubmission = uniq(
    DELETABLE_KINDS.flatMap((kind) =>
      recordsOf(lastSubmission, kind).map((record) => reasonByKey.get(`${kind}|${submittedKey(record)}`)),
    ).filter((reason): reason is string => !!reason),
  );
  const blocking = new Set(excludedFromLastSubmission);

  return {
    submission: kept,
    excludedReasons: uniq([...reasonByKey.values()]).filter((reason) => !blocking.has(reason)),
    excludedFromLastSubmission,
  };
}

export function unsyncedAssetLinkNames(submission: SalesforceSubmission) {
  const isUnsynced = (link: string | null) => !!link && isFallbackAssetId(link);
  return [
    ...new Set(
      [
        ...[...submission.deal_offers, ...submission.surcharge_periods].filter(({ room_asset }) =>
          isUnsynced(room_asset),
        ),
        ...submission.inclusions.filter(({ asset }) => isUnsynced(asset)),
      ].map(({ fields }) => String(fields.name)),
    ),
  ];
}

const inclusionFields = (draft: InclusionDraftPayload | undefined) => ({
  type__c: draft?.type ?? null,
  rrp__c: draft?.rrp ?? null,
  applies__c: draft?.applies ?? null,
  valid_for__c: draft?.validFor.join(';') || null,
  inclusion_description__c: draft?.description ?? null,
});

export function buildSalesforceSubmission(input: SalesforceSubmissionInput): SalesforceSubmission {
  const { preferredOption, propertySetup, optionState } = input;
  const currency = propertySetup?.currency ?? null;
  const createdRoomAssets = new Map(input.roomAssets.map((asset) => [asset.id, asset]));
  const createdInclusionAssets = new Map(input.inclusionAssets.map((asset) => [asset.id, asset]));

  const dealOffers: SalesforceSubmission['deal_offers'] = [];
  const bundledExperiences: SalesforceSubmission['bundled_experiences'] = [];
  optionState.roomsByCategory.forEach((rooms, categoryIndex) =>
    rooms
      .filter((room) => room.assetName)
      .forEach((room) => {
        const pricing = optionState.pricingByRoomId[room.id];
        if (!pricing) {
          return;
        }
        const occupancy = pricing.occupancy || PACKAGE_CATEGORIES[categoryIndex].defaultOccupancy;
        optionState.lengthsOfStay.forEach((nights) => {
          const sell = numberOrNull(pricing.sellByLos[nights]);
          if (!sell) {
            return;
          }
          const ref = `${room.id}::${nights}`;
          dealOffers.push({
            ref,
            room_asset: room.assetId || null,
            fields: {
              name: dealOptionName(nights, room.assetName, occupancy, room.suffix),
              price__c: sell,
              cost_price_gst__c: numberOrNull(pricing.netByLos[nights]),
              room_type__c: room.assetName,
              allocation__c: numberOrNull(pricing.allocation),
              number_of_nights__c: nights,
              nightly_sell_price__c: numberOrNull(deriveExtraNightSell(pricing, optionState.lengthsOfStay)),
              nightly_cost_price__c: numberOrNull(pricing.extraNightNet),
              currencyisocode: currency,
            },
          });
          (room.experiences ?? []).forEach((experience) =>
            bundledExperiences.push({
              ref: experience.id,
              deal_offer: ref,
              fields: {
                name: experience.dealOptionName,
                ancillary_deal_option__c: experience.id,
                currencyisocode: currency,
              },
            }),
          );
        });
      }),
  );
  const submittedDealOffers = new Set(dealOffers.map(({ ref }) => ref));

  const inclusionDrafts = input.inclusionDrafts.filter((draft) => draft.pricingOptionIndex === preferredOption);
  const packageRooms = getPackageRooms(optionState);
  const nights = getAvailableNights(optionState);
  const inclusions = inclusionDrafts.flatMap((draft) => {
    const assetId = draft.assetLink?.assetId;
    if (!assetId) {
      return [];
    }
    return packageRooms.flatMap((room) =>
      nights
        .filter((night) => packageMatchesApplyTo(room, night, draft.applyTo))
        .map((night) => `${room.id}::${night}`)
        .filter((ref) => submittedDealOffers.has(ref))
        .map((ref) => ({
          ref: draft.id,
          deal_offer: ref,
          asset: assetId,
          fields: {
            name: draft.name.trim(),
            ...inclusionFields(draft),
            currencyisocode: currency,
          },
        })),
    );
  });

  const usedInclusionAssetIds = new Set(inclusions.map(({ asset }) => asset));
  const inclusionAssets = [...createdInclusionAssets.values()]
    .filter((asset) => usedInclusionAssetIds.has(asset.id))
    .map((asset) => {
      const draft = inclusionDrafts.find(({ assetLink }) => assetLink?.assetId === asset.id);
      return {
        ref: asset.id,
        fields: {
          name: asset.name,
          currencyisocode: currency,
          ...inclusionFields(draft),
        },
      };
    });

  const optionRooms = egsRooms(optionState.roomsByCategory);
  const namedRooms = knownEgsRooms(input.opportunity, optionState.roomsByCategory, input.roomAssets);
  const extraGuestSurcharges = input.egs.map((draft) => ({
    ref: draft.id,
    fields: {
      name: getEgsRecordName(draft, namedRooms),
      extra_guest_type__c: draft.guestType,
      collected_by__c: draft.collectedBy,
      inclusions__c: draft.inclusions,
      bedding_type__c: draft.beddingType || null,
      other_extra_bedding_type__c: draft.otherBeddingType || null,
      // Salesforce Applies_To__c picklist is Daily / Per Stay; drafts store the "Daily Rate" label.
      applies_to__c: draft.rateType === 'Daily Rate' ? 'Daily' : draft.rateType || null,
      rooms__c: (draft.rooms === 'all' ? optionRooms.map(({ id }) => id) : draft.rooms)
        .map((roomId) => egsRoomName(namedRooms, roomId))
        .join(';'),
      from_age__c: draft.fromAge,
      to_age__c: draft.toAge,
      cost_price__c: draft.costPrice,
      converted_cost__c: draft.convertedCost,
      sell_price_aud__c: draft.sellPriceAud,
      currencyisocode: draft.currency || currency,
    },
  }));

  const surchargePeriods = input.surchargePeriods
    .filter((period) => period.options.includes(preferredOption))
    .flatMap((period) =>
      (period.rooms === 'all' ? optionRooms.map(({ id }) => id) : period.rooms).map((roomAssetId) => ({
        ref: `${period.id}::${roomAssetId}`,
        room_asset: roomAssetId,
        fields: {
          name: period.name.trim(),
          from__c: period.startDate,
          to__c: period.endDate,
          price__c: period.sellAud,
          cost__c: period.cost,
          applies_to_specific_days__c: yesNo(period.weekdays.length > 0),
          day_of_the_week__c: period.weekdays.map((day) => WEEKDAY_NAMES[day]).join(';') || null,
          collected_by__c: period.collectedBy,
          do_these_surcharges_accumulate__c: yesNo(period.accumulates),
          overlapping__c: period.overlapping,
          currencyisocode: period.currency || currency,
        },
      })),
    );

  const usedRoomAssetIds = new Set([...dealOffers, ...surchargePeriods].map(({ room_asset }) => room_asset));
  const roomAssets = [...createdRoomAssets.values()]
    .filter((asset) => usedRoomAssetIds.has(asset.id))
    .map((asset) => ({ ref: asset.id, fields: { name: asset.name, currencyisocode: currency } }));

  const overrideTiers = (input.overrides?.tiers ?? []).map((tier, index) => ({
    ref: tier.id,
    fields: {
      name: tier.name?.trim() || `Tier ${index + 1}`,
      from__c: tier.from ?? null,
      to__c: tier.to ?? null,
      commission__c: tier.comms ?? null,
      commission_based_on__c: input.overrides ? COMMISSION_BASIS_LABELS[input.overrides.basis] : null,
      currencyisocode: currency,
    },
  }));

  const propertySetupFields = {
    ac_deal_category__c: propertySetup?.dealCategory,
    ac_deal_sub_category__c: propertySetup?.offerCountry,
    destination__c: propertySetup?.destination,
    sub_destination__c: propertySetup?.subDestination,
    expiry_date_travel_from__c: propertySetup?.travelFrom,
    travel_to__c: propertySetup?.travelTo,
  };
  const opportunityFields = {
    ...propertySetupFields,
    currencyisocode: currency,
    room_nights_forecast__c: input.forecast?.roomNights,
    salesperson_gross_sales_forecast__c: input.forecast?.ttvAud,
    salesperson_margin_forecast__c: input.forecast?.grossMarginAud,
  };
  const sentFields = withoutBlanks(opportunityFields);
  // Only a Property Setup field an earlier submission set is cleared; a forecast still loading never is.
  const isCleared = (field: string) => field in propertySetupFields && input.previousOpportunity?.[field] != null;

  return {
    opportunity: Object.fromEntries(
      Object.keys(opportunityFields)
        .filter((field) => field in sentFields || isCleared(field))
        .map((field) => [field, sentFields[field] ?? null]),
    ),
    room_assets: roomAssets,
    inclusion_assets: inclusionAssets,
    blackouts: input.blackouts.map((blackout) => ({
      ref: blackout.id,
      fields: { name: blackout.name.trim(), from__c: blackout.startDate, to__c: blackout.endDate },
    })),
    extra_guest_surcharges: extraGuestSurcharges,
    override_tiers: overrideTiers,
    deal_offers: dealOffers,
    surcharge_periods: surchargePeriods,
    inclusions,
    bundled_experiences: bundledExperiences,
  };
}
